import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Header.css';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Check if user is signed in from localStorage
    const authStatus = localStorage.getItem('isSignedIn') === 'true';
    setIsSignedIn(authStatus);
  }, []);

  // Listen for storage changes (when user logs in from another tab)
  useEffect(() => {
    const handleStorageChange = () => {
      const authStatus = localStorage.getItem('isSignedIn') === 'true';
      setIsSignedIn(authStatus);
    };

    window.addEventListener('storage', handleStorageChange);
    // Also listen for custom event for same-tab updates
    window.addEventListener('authStatusChange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authStatusChange', handleStorageChange);
    };
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && 
          !event.target.closest('.header-container') && 
          !event.target.closest('.mobile-menu-toggle') &&
          !event.target.closest('.header-nav') &&
          !event.target.closest('.header-actions')) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    localStorage.setItem('isSignedIn', 'false');
    setIsSignedIn(false);
    navigate('/');
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('authStatusChange'));
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="header">
      <div className="header-container">
        <div className="header-logo">
          <Link to="/" className="logo-link" onClick={closeMobileMenu}>
            <img 
              src="https://wnsyglbyunngvlcpdhju.supabase.co/storage/v1/object/public/Images/1-01.png" 
              alt="SymbolFly Logo" 
              className="logo-image"
            />
          </Link>
        </div>
        
        <button 
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
        </button>
        
        <nav className={`header-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <Link 
            to="/" 
            className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            Home
          </Link>
          
          <Link 
            to="/local-flights" 
            className={`nav-link ${location.pathname === '/local-flights' ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            Local Flights
          </Link>
          
          <Link 
            to="/international-flights" 
            className={`nav-link ${location.pathname === '/international-flights' ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            International Flights
          </Link>
          
          {isSignedIn ? (
            <>
              <Link 
                to="/book-flight" 
                className={`nav-link ${location.pathname === '/book-flight' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Book a Flight
              </Link>
              <Link 
                to="/private-jets" 
                className={`nav-link ${location.pathname === '/private-jets' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Private Jets
              </Link>
              <Link 
                to="/hotels" 
                className={`nav-link ${location.pathname === '/hotels' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Hotels
              </Link>
              <Link 
                to="/dashboard" 
                className={`nav-link ${location.pathname === '/dashboard' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                My Trips
              </Link>
              <Link 
                to="/profile" 
                className={`nav-link ${location.pathname === '/profile' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Profile
              </Link>
              <Link 
                to="/support" 
                className={`nav-link ${location.pathname === '/support' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Support
              </Link>
              <button 
                className="nav-link logout-mobile"
                onClick={() => {
                  handleLogout();
                  closeMobileMenu();
                }}
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link 
                to="/book-flight" 
                className={`nav-link ${location.pathname === '/book-flight' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Book a Flight
              </Link>
              <Link 
                to="/private-jets" 
                className={`nav-link ${location.pathname === '/private-jets' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Private Jets
              </Link>
              <Link 
                to="/hotels" 
                className={`nav-link ${location.pathname === '/hotels' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Hotels
              </Link>
              <Link 
                to="/contact" 
                className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Contact
              </Link>
              <Link 
                to="/about" 
                className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                About
              </Link>
              <Link 
                to="/login" 
                className="nav-link mobile-login-link"
                onClick={closeMobileMenu}
              >
                Login
              </Link>
            </>
          )}
        </nav>
        
        <div className={`header-actions ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          {isSignedIn ? (
            <button 
              className="logout-button"
              onClick={() => {
                handleLogout();
                closeMobileMenu();
              }}
            >
              Logout
            </button>
          ) : (
            <Link 
              to="/login" 
              className="signup-button-link"
              onClick={closeMobileMenu}
            >
              <button className="signup-button">Login</button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;

